import { useState, useRef, useEffect } from "react";
import { Loader2, Mail, MapPin, Clock, MessageCircle, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { dayKeyMtl } from "../lib/dateMtl";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp, MaskedLine } from "../components/motion";
import { ShareButtons } from "../components/ShareButtons";
import { ELink } from "../components/Editable";
import { CALLBACK_HOURS, buildCallbackHours } from "../components/CallRequestDialog";
import RendezvousFormulaire from "../components/RendezvousFormulaire";
import RichText from "../components/RichText";
import { SEO } from "../components/SEO";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

export default function Contact() {
  const { t, ts, tx, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [callMode, setCallMode] = useState(false); // « Demander un appel »
  const [callForm, setCallForm] = useState({ first_name: "", last_name: "", phone: "", postal_code: "", organization: "", callback_date: "", callback_from: "", callback_to: "", message: "" });
  const setCall = (k) => (e) => setCallForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));
  // Heures de rappel RÉGLABLES (Admin → Paramètres) — bornent les listes De/À
  const [callHours, setCallHours] = useState(CALLBACK_HOURS);
  useEffect(() => {
    api.get("/settings/shop").then((r) => {
      const ch = r.data?.callback_hours;
      if (ch?.start && ch?.end) setCallHours(buildCallbackHours(ch.start, ch.end));
    }).catch(() => {});
  }, []);
  const formRef = useRef(null);

  const startCallRequest = () => {
    setCallMode(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const submitCall = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/call-request", callForm);
      toast.success(t("contact.call_sent"));
      setCallForm({ first_name: "", last_name: "", phone: "", postal_code: "", organization: "", callback_date: "", callback_from: "", callback_to: "", message: "" });
      setCallMode(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const INFO = [
    { icon: Mail, text: <RichText text={tx("contact.info_email", "info@trophee.ca")} path="contact.info_email" />, href: "mailto:info@trophee.ca" },
    { icon: MapPin, text: t("contact.region") },
    { icon: Phone, text: t("contact.phone"), href: `tel:${ts("contact.phone").replace(/[^+\d]/g, "")}` },
    { icon: MessageSquare, text: `${ts("contact.texto")} — ${ts("contact.phone")}`, href: `sms:${ts("contact.phone").replace(/[^+\d]/g, "")}` },
    { icon: Clock, text: t("contact.hours") },
    { icon: MessageCircle, text: t("contact.social"), href: "https://m.me/trophee.ca" },
  ];

  return (
    <div data-testid="contact-page" className="pt-32 pb-28 spotlight">
      <SEO title={ts("seo.contact_title")} description={ts("seo.contact_desc")} path="/contact"
        jsonLd={{ "@context": "https://schema.org", "@type": "ContactPage",
                  name: ts("seo.contact_title"), url: `https://mrtrophee.ca/contact`,
                  inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
                  isPartOf: { "@type": "WebSite", url: "https://mrtrophee.ca" },
                  about: { "@id": "https://mrtrophee.ca/#entreprise" } }} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("contact.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight mb-6">
            <MaskedLine key={ts("contact.title_1")} delay={0.15}>{t("contact.title_1")}</MaskedLine>
            <MaskedLine key={ts("contact.title_2")} delay={0.35} className="italic text-gold">{t("contact.title_2")}</MaskedLine>
          </h1>
          <p className="text-ivory/60 max-w-xl mb-8">{t("contact.sub")}</p>
          <div className="mb-8">
            <ShareButtons title={`${ts("contact.title_1")} ${ts("contact.title_2")} — Mr Trophée`} label={t("footer.share_page")} />
          </div>
          <div className="flex flex-wrap gap-4 mb-14">
            <button
              onClick={startCallRequest} data-testid="contact-call-cta"
              className="inline-flex items-center gap-3 bg-gold text-ink font-bold px-8 py-4 rounded-full hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              {ts("contact.call_cta").toUpperCase()}
            </button>
            <ELink
              editPath="link.messenger"
              to="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="contact-top-messenger"
              className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              {t("contact.messenger")}
            </ELink>
          </div>
        </FadeUp>

        <div className="grid md:grid-cols-5 gap-12">
          <FadeUp delay={0.1} className="md:col-span-3">
            {callMode ? (
              <form ref={formRef} onSubmit={submitCall} className="bg-surface border border-gold/40 rounded-2xl p-8 space-y-5" data-testid="call-form">
                <div>
                  <h3 className="font-serif text-2xl text-gold">{t("contact.call_title")}</h3>
                  <p className="text-sm text-ivory/60 mt-1">{t("contact.call_sub")}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-ivory/70 mb-2">{t("contact.first_name")}</label>
                    <input
                      data-testid="call-first-name" required value={callForm.first_name} onChange={setCall("first_name")}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-ivory/70 mb-2">{t("contact.last_name")}</label>
                    <input
                      data-testid="call-last-name" required value={callForm.last_name} onChange={setCall("last_name")}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-2">{t("contact.call_phone")}</label>
                  <input
                    data-testid="call-phone" type="tel" required minLength={7} value={callForm.phone} onChange={setCall("phone")}
                    placeholder="+1 514-…"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-ivory/70 mb-2">{t("contact.call_postal")} *</label>
                    <input
                      data-testid="call-postal" required minLength={6} maxLength={7} value={callForm.postal_code} onChange={setCall("postal_code")}
                      placeholder="A1A 1A1"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-ivory/70 mb-2">{t("contact.call_org")}</label>
                    <input
                      data-testid="call-org" value={callForm.organization} onChange={setCall("organization")}
                      placeholder={ts("contact.call_org_placeholder")}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-2">{t("contact.call_time")}</label>
                  {/* Agenda de rappel (demande du propriétaire) : date + plage horaire
                      — crée un événement « Rappel » dans son Google Agenda si connecté */}
                  <div className="grid grid-cols-[1fr_5.4rem_5.4rem] gap-2">
                    <input type="date" data-testid="call-date" value={callForm.callback_date}
                      min={dayKeyMtl()} onChange={setCall("callback_date")}
                      aria-label={ts("contact.call_date")}
                      className="w-full min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-3.5 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors duration-300" />
                    <select data-testid="call-from" value={callForm.callback_from} onChange={setCall("callback_from")}
                      aria-label={ts("contact.call_from")}
                      className="bg-ink border border-ivory/15 rounded-lg px-2 py-3.5 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors duration-300">
                      <option value="">{ts("contact.call_from")}</option>
                      {callHours.map((h) => <option key={h.v} value={h.v}>{lang === "en" ? h.en : h.fr}</option>)}
                    </select>
                    <select data-testid="call-to" value={callForm.callback_to} onChange={setCall("callback_to")}
                      aria-label={ts("contact.call_to")}
                      className="bg-ink border border-ivory/15 rounded-lg px-2 py-3.5 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors duration-300">
                      <option value="">{ts("contact.call_to")}</option>
                      {callHours.map((h) => <option key={h.v} value={h.v}>{lang === "en" ? h.en : h.fr}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-2">{t("contact.call_subject")}</label>
                  <textarea
                    data-testid="call-message" rows={3}
                    value={callForm.message} onChange={setCall("message")}
                    placeholder={ts("contact.call_subject_placeholder")}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
                  />
                </div>
                <button
                  data-testid="call-submit" type="submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                  {loading ? t("contact.sending") : t("contact.call_send")}
                </button>
                <button type="button" onClick={() => setCallMode(false)} data-testid="call-back"
                  className="w-full text-center text-xs text-ivory/55 hover:text-gold underline underline-offset-2 transition-colors">
                  {t("contact.call_back")}
                </button>
              </form>
            ) : (
            /* Demande du propriétaire (2026-10-20) : le formulaire de message est
               REMPLACÉ par la prise de rendez-vous (même parcours que /rendez-vous) */
            <div ref={formRef}>
              <RendezvousFormulaire />
            </div>
            )}
          </FadeUp>

          <FadeUp delay={0.2} className="md:col-span-2">
            <div className="bg-surface/60 border border-ivory/10 rounded-2xl p-8">
              <h3 className="font-serif text-2xl mb-6">{t("contact.info_title")}</h3>
              <ul className="space-y-5">
                {INFO.map((item, i) => (
                  <li key={i} className="flex items-center gap-4 text-sm text-ivory/70">
                    <item.icon className="w-5 h-5 text-gold shrink-0" />
                    {item.href ? (
                      <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                         data-testid={`contact-info-${i}`} className="hover:text-gold transition-colors duration-300">
                        {item.text}
                      </a>
                    ) : item.text}
                  </li>
                ))}
              </ul>
              <ELink
                editPath="link.messenger"
                to="https://m.me/trophee.ca" target="_blank" rel="noreferrer"
                data-testid="contact-messenger-cta"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" /> {t("contact.messenger")}
              </ELink>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="mt-14">
          <div className="card-clip overflow-hidden border border-gold/15" data-testid="contact-map">
            <iframe
              title="Mr Trophée — Google Maps"
              src="https://www.google.com/maps?q=Mr+Troph%C3%A9e,+1314+Ch+du+Lac+Saint+Louis,+L%C3%A9ry,+QC+J6N+1B1&output=embed"
              className="w-full h-[400px] grayscale-[30%] contrast-[1.05]"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-ivory/65 mt-3 italic">{t("contact.region")}</p>
        </FadeUp>
      </div>
    </div>
  );
}
